import { Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/status-badge/status-badge';
import VsTable from '@/components/vs-table/vs-table';
import { CampaignEntry } from '@/services/outbound-calls-service/campaigns.service.types';
import { CallOutcome, Conclusion } from '../call-result';
import { entryErrorLabel } from '../outbound-calls.messages';
import { ENTRY_STATUS_BADGE, formatDateTime } from '../outbound-calls.utils';
import styles from '../outbound-calls.module.scss';

interface CampaignEntriesTableProps {
  entries: CampaignEntry[];
  maxAttempts: number;
  loading: boolean;
  onOpenLead: (leadId: number) => void;
}

/** One row per number: a contact's, or the company's own when the lead has no contact. */
export function CampaignEntriesTable({
  entries,
  maxAttempts,
  loading,
  onOpenLead,
}: CampaignEntriesTableProps) {
  const { t } = useTranslation('outbound');

  const columns: ColumnsType<CampaignEntry> = [
    {
      title: t('entries.columns.lead'),
      key: 'lead',
      render: (_, entry) => (
        <span className={styles.stack}>
          <b>{entry.lead.name}</b>
          <span className={styles.secondary}>
            {[entry.lead.nip, entry.lead.industry].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      title: t('entries.columns.contact'),
      key: 'contact',
      render: (_, entry) =>
        entry.contact ? (
          <span className={styles.stack}>
            <span>{entry.contact.full_name}</span>
            <span className={styles.secondary}>{entry.contact.position}</span>
          </span>
        ) : (
          <span className={styles.secondary}>{t('entries.switchboard')}</span>
        ),
    },
    {
      title: t('entries.columns.phone'),
      key: 'phone',
      render: (_, entry) => (
        <span className={styles.stack}>
          <span>{entry.phone}</span>
          {entry.do_not_call && <StatusBadge variant="fail" label={t('entries.doNotCall')} />}
          {entry.wrong_number && <StatusBadge variant="hand" label={t('entries.wrongNumber')} />}
        </span>
      ),
    },
    {
      title: t('entries.columns.status'),
      key: 'status',
      render: (_, entry) => (
        <span className={styles.stack}>
          <StatusBadge
            variant={ENTRY_STATUS_BADGE[entry.status]}
            label={t(entry.callback_at ? 'entryStatus.callback' : `entryStatus.${entry.status}`)}
          />
          {entry.callback_at && (
            <span className={styles.secondary}>
              {t('entries.callbackAt', { at: formatDateTime(entry.callback_at) })}
            </span>
          )}
        </span>
      ),
    },
    {
      title: t('entries.columns.attempts'),
      key: 'attempts',
      render: (_, entry) => (
        <span className={styles.stack}>
          <span>{`${entry.attempts}/${maxAttempts}`}</span>
          {entry.callbacks > 0 && (
            <span className={styles.secondary}>
              {t('entries.callbacks', { callbacks: entry.callbacks })}
            </span>
          )}
        </span>
      ),
    },
    {
      title: t('entries.columns.lastAttempt'),
      key: 'last_called_at',
      render: (_, entry) => (
        <span className={styles.stack}>
          <span>{formatDateTime(entry.last_called_at)}</span>
          {entry.next_attempt_at && !entry.callback_at && (
            <span className={styles.secondary}>
              {t('entries.nextAttempt', { at: formatDateTime(entry.next_attempt_at) })}
            </span>
          )}
        </span>
      ),
    },
    {
      title: t('entries.columns.outcome'),
      key: 'outcome',
      render: (_, entry) => (
        <span className={styles.stack}>
          {entry.last_call ? (
            <>
              <CallOutcome
                disposition={entry.last_call.disposition}
                objectiveCompleted={entry.last_call.objective_completed}
                live={entry.status === 'calling'}
              />
              <Conclusion conclusion={entry.last_call.conclusion} clamp />
            </>
          ) : (
            <span className={styles.secondary}>{t('none')}</span>
          )}
          {entry.last_error && (
            <span className={`${styles.danger} ${styles.clamp}`}>
              {entryErrorLabel(entry.last_error)}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <VsTable<CampaignEntry>
      rowKey="id"
      loading={loading}
      dataSource={entries}
      columns={columns}
      scroll={{ x: 1000 }}
      onRow={(entry) => ({
        onClick: () => onOpenLead(entry.lead.id),
        className: styles.clickable,
      })}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('entries.empty')} />,
      }}
    />
  );
}
